import React, { useEffect, useState } from "react";
import { Table, Select, Input, Space } from "antd";
import { SearchOutlined } from "@ant-design/icons";
import useRequest from "../../hooks/useRequest";
import apiPath from "../../constants/apiPath";
import EmptyState from "../../components/ui/EmptyState";

const thisYear = new Date().getFullYear();
const YEARS = [thisYear - 1, thisYear, thisYear + 1];

// Each group (Annual / Sick / YTD) gets its own soft header tint and a neutral grey line before it,
// so the groups read as separate blocks without heavy lines.
const GROUP = {
  annual: { tint: "#F3EFFE" },
  sick: { tint: "#FFF6E3" },
  ytd: { tint: "#EAF2F9" },
};
const LINE = "1px solid #C3CDD8";
const startOfGroup = { onCell: () => ({ style: { borderLeft: LINE } }) };
const headerOf = (group, extra = {}) => () => ({ style: { background: GROUP[group].tint, ...extra } });

// Total / taken / pending / left columns for one leave type.
const kpiCol = (title, key, group) => ({
  title,
  onHeaderCell: headerOf(group, { borderLeft: LINE }),
  children: [
    { title: "Total", dataIndex: [key, "entitled"], width: 70, align: "center", onHeaderCell: headerOf(group, { borderLeft: LINE }), ...startOfGroup },
    { title: "Taken", dataIndex: [key, "taken"], width: 70, align: "center", onHeaderCell: headerOf(group) },
    { title: "Pending", dataIndex: [key, "pending"], width: 80, align: "center", onHeaderCell: headerOf(group) },
    { title: "Left", dataIndex: [key, "remaining"], width: 70, align: "center", render: (v) => <strong>{v}</strong>, onHeaderCell: headerOf(group) },
  ],
});

const LeaveBalances = () => {
  const { request } = useRequest();
  const [year, setYear] = useState(thisYear);
  const [rows, setRows] = useState([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setLoading(true);
    request({
      url: `${apiPath.leaveBalances}?year=${year}`,
      method: "GET",
      onSuccess: (res) => {
        setRows(res?.data?.rows || []);
        setLoading(false);
      },
      onError: () => setLoading(false),
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [year]);

  const q = search.trim().toLowerCase();
  const data = q ? rows.filter((r) => `${r.name} ${r.employeeId} ${r.department}`.toLowerCase().includes(q)) : rows;

  const columns = [
    {
      title: "Employee",
      dataIndex: "name",
      fixed: "left",
      width: 220,
      render: (v, r) => (
        <div>
          <div style={{ fontWeight: 600 }}>{v}</div>
          <div style={{ color: "#6B7280", fontSize: 12 }}>
            {r.employeeId}
            {r.department ? ` · ${r.department}` : ""}
          </div>
        </div>
      ),
    },
    { title: "Joined", dataIndex: "joiningDate", width: 110, render: (v) => v || "-" },
    kpiCol("Annual leave", "annual", "annual"),
    kpiCol("Sick leave", "sick", "sick"),
    { title: "Leave taken (YTD)", dataIndex: "totalTakenYtd", width: 140, align: "center", onHeaderCell: headerOf("ytd", { borderLeft: LINE }), ...startOfGroup },
  ];

  return (
    <>
      <Space style={{ marginBottom: 16 }} wrap>
        <Select value={year} onChange={setYear} style={{ width: 110 }} options={YEARS.map((y) => ({ value: y, label: y }))} />
        <Input allowClear prefix={<SearchOutlined />} placeholder="Search employee" style={{ width: 240 }} value={search} onChange={(e) => setSearch(e.target.value)} />
        <span style={{ color: "#6B7280", fontSize: 12 }}>
          Total = yearly allowance (pro-rated for mid-year joiners). Taken = approved days. Left = Total minus Taken. YTD = leave days already taken up to today, all types.
        </span>
      </Space>
      <Table
        rowKey="_id"
        bordered
        size="middle"
        loading={loading}
        columns={columns}
        dataSource={data}
        scroll={{ x: 900 }}
        pagination={{ pageSize: 20 }}
        locale={{ emptyText: <EmptyState message="No employees found" /> }}
      />
    </>
  );
};

export default LeaveBalances;
